/**
 * The tour (W2-14 onboarding-wizard; DESIGN.md §5.14; `DIALOG.tour`, a non-modal surface): eight coach marks over
 * the live editor (tourSteps.ts). Skippable (Skip, the close button or Escape in the card), replayable from Help and
 * the palette (`tour.start`, `startAt` picks the first step), aware of reduced motion. It puts the preview in Edit
 * mode first. A step that watches for the user's action marks itself done when it happens and moves on after a
 * short pause; Next / Back always work. The card never covers the area a step names as `avoid` (for the Tap and
 * Hold steps that is the tool rail the ringed button sits on: the card then lands on the rail's right edge, beside
 * the button and over the top of the lane, leaving the rest of the lane free to click in), and a step's `prefer`
 * names the side of the target to try first (UX-095 side placement).
 * While Theater or test play covers the window, or the step's part is not in the workspace the user is in, the card
 * is replaced by a compact "Step n of 8" chip at the bottom left (CoachMark `docked`, round-2 UX): it never sits on
 * the primary actions of another screen (the Publish export bar) and never claims the target will "appear" while
 * blocking the view of it.
 * Finishing or skipping records `general.tourDone`.
 *
 * Keyboard: the steps ask for editor keys (play, tools, place at the mouse, the curve picker, Publish), so the
 * card never holds those keys back:
 *  - the card is not a dialog (CoachMark), so focus inside it keeps every global binding working;
 *  - focus goes into the card when the tour starts (screen readers hear it, Enter takes the first Next). On a step
 *    that waits for an editor action, focus that is inside the card moves to the card itself, never to a button:
 *    Space on a keyboard-focused Next would press it (and skip the step), on the card it reaches Play. On a step
 *    without an action, focus inside the card goes to Next, so Enter walks on. Focus the user put in the editor
 *    stays there (Back / Next never take focus on a click). Focus that was lost (on the page itself, e.g. because
 *    the focused part of the editor was unmounted by the step's action) counts as focus inside the card. When the
 *    last step's action is done, Finish gets focus (also after F4 replaced the Chart workspace that held it).
 *  - the keys a step names are the ones the active keymap binds now (keys.tsx), drawn as key caps.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AppServices, DialogProps } from '../../app/registry/types';
import { Button, Icon, IconButton } from '../../ui';
import { CoachMark } from './CoachMark';
import { prefersReducedMotion, useServicesText, useSettingsValue } from './hooks';
import { bindingsOf, keyLabelParam, keyParams, KeyText, useKeyBindings } from './keys';
import { m } from './onboarding.messages';
import { tourActive } from './onboardingState';
import s from './Onboarding.module.css';
import { findHelpTarget } from './showMe';
import { TOUR_STEPS, type TourStepDef } from './tourSteps';

/** Pause between a completed step and the next one (ms). */
export const TOUR_ADVANCE_MS = 1200;

function clampIndex(i: number | undefined): number {
  if (i === undefined || !Number.isFinite(i)) return 0;
  return Math.max(0, Math.min(TOUR_STEPS.length - 1, Math.floor(i)));
}

/** Delays after a step change or completion at which lost focus is looked for again (ms). */
const FOCUS_RECHECK_MS: readonly number[] = [0, 250];

/**
 * Is keyboard focus nowhere in particular: on the page itself, or on an element that has left the document (the
 * focused part of the editor was unmounted, e.g. when a key switched workspaces)? The tour then treats it like focus
 * inside its card.
 */
export function focusLost(active: Element | null): boolean {
  return active === null || active === document.body || active === document.documentElement || !active.isConnected;
}

/**
 * Does a full-window surface cover the editor: Theater or test play (the coach mark stands back for it, round-2
 * UX). The when-atoms come from the registry when it exposes them (`whenContext`, the app registry's own extra);
 * the editor state alone answers Theater without one.
 */
export function fullWindowUp(services: Pick<AppServices, 'editor' | 'registry'>): boolean {
  if (services.editor.getState().preview.theater) return true;
  try {
    const extras = services.registry as { whenContext?: () => { has(atom: string): boolean } };
    const w = extras.whenContext?.();
    return w !== undefined && (w.has('testPlay') || w.has('theater'));
  } catch {
    return false;
  }
}

/** The card's primary button (Next / Finish). */
const PRIMARY = '[data-tour-primary]';

/**
 * Where keyboard focus belongs in the card for `step` (see the module header): the card itself while the step
 * waits for an editor action (a done step other than the last moves on by itself, so the card keeps it), else the
 * primary button (Next, or Finish once the last step is done).
 */
export function tourFocusTarget(
  card: HTMLElement,
  step: Pick<TourStepDef, 'watch'>,
  done: boolean,
  last: boolean,
): HTMLElement {
  if (step.watch !== undefined && !(done && last)) return card;
  return card.querySelector<HTMLElement>(PRIMARY) ?? card;
}

export function Tour(p: DialogProps<'tour'>) {
  const { services } = p;
  const text = useServicesText(services);
  const settings = useSettingsValue(services);
  const bindings = useKeyBindings(services.keymap);
  const reduced = prefersReducedMotion(settings);
  const total = TOUR_STEPS.length;
  const [index, setIndex] = useState(() => clampIndex(p.props.startAt));
  const [completed, setCompleted] = useState<ReadonlySet<string>>(() => new Set());
  const [anchor, setAnchor] = useState<{
    readonly step: string;
    readonly el: Element | null;
    readonly avoid: Element | null;
  } | null>(null);
  // Theater / test play cover the window: the card stands back for a compact chip (round-2 UX).
  const [overlay, setOverlay] = useState(() => fullWindowUp(services));
  const finished = useRef(false);
  const card = useRef<HTMLDivElement | null>(null);
  const onCard = useCallback((el: HTMLDivElement | null) => {
    card.current = el;
  }, []);
  const started = useRef(false);
  const step = TOUR_STEPS[index] ?? TOUR_STEPS[0];
  const isDone = step !== undefined && completed.has(step.id);
  const last = index === total - 1;

  useEffect(() => {
    tourActive.set(true);
    return () => {
      tourActive.set(false);
    };
  }, []);

  useEffect(() => {
    step?.prepare?.(services);
  }, [step, services]);

  // Keyboard focus (module header): into the card when the tour starts; afterwards it is only moved while it is
  // inside the card or lost (see `focusLost`), never taken from the editor. Focus may be lost a moment after the
  // step's action (the action unmounts the part that held it, e.g. the Publish key replacing the Chart workspace),
  // so the check runs again shortly after.
  useEffect(() => {
    const c = card.current;
    if (c === null || step === undefined) return undefined;
    const first = !started.current;
    started.current = true;
    const place = (force: boolean): void => {
      const active = document.activeElement;
      if (!force && !focusLost(active) && !c.contains(active)) return;
      const target = tourFocusTarget(c, step, isDone, last);
      if (active !== target && target.isConnected) target.focus({ preventScroll: true });
    };
    place(first);
    const timers = FOCUS_RECHECK_MS.map((ms) =>
      window.setTimeout(() => {
        place(false);
      }, ms),
    );
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [step, isDone, last]);

  // The step's part of the editor, looked up again while the layout changes (panels open, workspaces switch).
  useEffect(() => {
    if (step === undefined) return undefined;
    let live = true;
    const tick = (): void => {
      if (!live) return;
      const el = findHelpTarget(services, step.target);
      const avoid = step.avoid === undefined ? null : findHelpTarget(services, step.avoid);
      setAnchor((prev) =>
        prev !== null && prev.step === step.id && prev.el === el && prev.avoid === avoid
          ? prev
          : { step: step.id, el, avoid },
      );
      setOverlay(fullWindowUp(services));
    };
    const first = window.setTimeout(tick, 0);
    const timer = window.setInterval(tick, 300);
    return () => {
      live = false;
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [step, services]);

  // Completion of the step's action, then the next step after a short pause. A layout effect, so the watch is
  // attached in the same commit that shows the step: an action taken right after the card appears (before passive
  // effects run) is not missed by the commit-based watches.
  useLayoutEffect(() => {
    if (step?.watch === undefined) return undefined;
    let advance: number | undefined;
    const id = step.id;
    const off = step.watch(services, () => {
      setCompleted((prev) => (prev.has(id) ? prev : new Set([...prev, id])));
      if (advance === undefined && index < total - 1) {
        advance = window.setTimeout(() => {
          setIndex((i) => (i === index ? i + 1 : i));
        }, TOUR_ADVANCE_MS);
      }
    });
    return () => {
      off();
      if (advance !== undefined) window.clearTimeout(advance);
    };
  }, [step, index, total, services]);

  if (step === undefined) return null;

  const finish = (done: boolean): void => {
    if (finished.current) return;
    finished.current = true;
    if (!services.settings.value.general.tourDone) services.settings.set('general', 'tourDone', true);
    if (done) {
      const help = keyLabelParam(services.keymap, { command: 'help.open' });
      services.ui.toast({ key: m.tourFinished, params: { help } }, { kind: 'success' });
    }
    p.close(null);
  };

  const current = anchor !== null && anchor.step === step.id ? anchor : null;
  const target = current?.el ?? null;
  // Docked chip: Theater or test play is up, or the step's part is not in this workspace (round-2 UX). The card
  // must never sit on another screen's primary actions (the Publish export bar) or over a full-window view.
  // Only a lookup that already ran for THIS step docks: while a step change is still being looked up the card
  // stands as usual.
  const lookedUp = anchor !== null && anchor.step === step.id;
  const docked = overlay || (lookedUp && target === null);
  const title = text.t(step.title);
  const label = text.t(m.tourStep, { n: index + 1, total });
  const body = keyParams(bindingsOf(bindings), step.keys ?? {});
  const bodyKey = target !== null && step.altBody?.when(target) === true ? step.altBody.body : step.body;

  const advance = (): void => {
    if (last) finish(true);
    else setIndex((i) => Math.min(total - 1, i + 1));
  };

  return (
    <CoachMark
      target={target}
      avoid={current?.avoid ?? null}
      prefer={step.prefer}
      title={title}
      label={label}
      announce={text.t(m.tourAnnounce, { label, title })}
      closeLabel={{ key: m.tourClose }}
      onClose={() => {
        finish(false);
      }}
      reducedMotion={reduced}
      onCard={onCard}
      docked={docked}
      chip={
        <>
          <Button size="sm" variant="primary" icon={last ? 'Check' : 'ChevronRight'} data-tour-primary="" onClick={advance}>
            {text.t(last ? m.tourFinish : m.tourNext)}
          </Button>
          <IconButton icon="X" size="sm" label={{ key: m.tourClose }} onClick={() => { finish(false); }} />
        </>
      }
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              finish(false);
            }}
          >
            {text.t(m.tourSkip)}
          </Button>
          <span className={s.spacer} />
          <Button
            size="sm"
            icon="ChevronLeft"
            onClick={() => {
              setIndex((i) => Math.max(0, i - 1));
            }}
            disabledReason={index === 0 ? { key: m.tourFirstStep } : null}
          >
            {text.t(m.tourBack)}
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={last ? 'Check' : 'ChevronRight'}
            data-tour-primary=""
            onClick={advance}
          >
            {text.t(last ? m.tourFinish : m.tourNext)}
          </Button>
        </>
      }
    >
      <p className={s.coachText}>
        <KeyText text={text.t(bodyKey, body.params)} chords={body.chords} />
      </p>
      {isDone && (
        <p className={s.coachDone} role="status">
          <Icon name="CircleCheck" size={16} />
          <span>{text.t(m.tourDoneStep)}</span>
        </p>
      )}
      {target === null && anchor !== null && (
        <p className={s.coachHint}>
          <Icon name="Info" size={14} />
          <span>{text.t(m.tourNotOnScreen)}</span>
        </p>
      )}
      <ol className={s.coachDots} aria-hidden="true">
        {TOUR_STEPS.map((st, i) => (
          <li key={st.id} data-state={i === index ? 'current' : completed.has(st.id) ? 'done' : undefined} />
        ))}
      </ol>
    </CoachMark>
  );
}
